import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, BookOpen } from "lucide-react";
import { useSEO } from "../lib/useSEO";
import { INSIGHTS, CATEGORIES } from "../lib/insightsData";
import { SITE_URL } from "../lib/seo";

const ALL = { slug: "all", label: "All" };

export default function InsightsPage() {
    useSEO({
        title: "Insights | Hospitality Operations Thinking — Optimum Hospitality Solutions",
        description: "Operator voiced articles on hospitality operations, labour productivity, performance and guest experience. Written by Sidney Adams for UK hotel owners, general managers and operations directors.",
        path: "/insights",
    });

    const [filter, setFilter] = useState("all");
    const filtered = useMemo(
        () => (filter === "all" ? INSIGHTS : INSIGHTS.filter((a) => a.category === filter)),
        [filter]
    );

    const breadcrumbSchema = {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        itemListElement: [
            { "@type": "ListItem", position: 1, name: "Home", item: `${SITE_URL}/` },
            { "@type": "ListItem", position: 2, name: "Insights", item: `${SITE_URL}/insights` },
        ],
    };
    const itemListSchema = {
        "@context": "https://schema.org",
        "@type": "ItemList",
        itemListElement: INSIGHTS.map((a, i) => ({
            "@type": "ListItem",
            position: i + 1,
            url: `${SITE_URL}/insights/${a.slug}`,
            name: a.title,
        })),
    };

    return (
        <div data-testid="insights-page">
            <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbSchema) }} />
            <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(itemListSchema) }} />

            <section className="border-b border-brand-border">
                <div className="mx-auto max-w-7xl px-6 lg:px-12 py-20 lg:py-28">
                    <span className="text-xs uppercase tracking-[0.25em] text-brand-champagne font-medium">Insights</span>
                    <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl mt-6 leading-[1.02] tracking-tight text-brand-navy">
                        Practical thinking, written by an operator.
                    </h1>
                    <p className="mt-7 text-lg leading-relaxed text-brand-ink/75 max-w-3xl">
                        Short articles on the operational levers that move performance in UK hotels and hospitality businesses. No buzzwords, no padding, no listicles. Written for owners, general managers and operations directors.
                    </p>
                </div>
            </section>

            <section className="border-b border-brand-border bg-brand-stone">
                <div className="mx-auto max-w-7xl px-6 lg:px-12 py-6 flex flex-wrap gap-2" data-testid="insights-filters">
                    {[ALL, ...CATEGORIES].map((c) => (
                        <button
                            key={c.slug}
                            data-testid={`insights-filter-${c.slug}`}
                            onClick={() => setFilter(c.slug)}
                            aria-pressed={filter === c.slug}
                            className={`px-4 py-2 text-xs uppercase tracking-[0.18em] border transition-colors ${
                                filter === c.slug
                                    ? "bg-brand-navy text-brand-stone border-brand-navy"
                                    : "bg-transparent text-brand-ink/70 border-brand-border hover:border-brand-champagne hover:text-brand-navy"
                            }`}
                        >
                            {c.label}
                        </button>
                    ))}
                </div>
            </section>

            <section>
                <div className="mx-auto max-w-7xl px-6 lg:px-12 py-16 lg:py-20">
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-brand-border" data-testid="insights-grid">
                        {filtered.map((a) => (
                            <Link
                                key={a.slug}
                                to={`/insights/${a.slug}`}
                                data-testid={`insight-card-${a.slug}`}
                                className="bg-brand-stone p-8 lg:p-10 flex flex-col hover:bg-white transition-colors"
                            >
                                <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.25em] text-brand-champagne">
                                    <BookOpen size={12} />
                                    {CATEGORIES.find((c) => c.slug === a.category)?.label}
                                </div>
                                <h2 className="font-display text-xl lg:text-2xl mt-4 text-brand-navy leading-snug">{a.title}</h2>
                                <p className="mt-4 text-sm text-brand-ink/75 leading-relaxed flex-1">{a.excerpt}</p>
                                <div className="mt-6 flex items-center justify-between">
                                    <span className="text-xs text-brand-ink/55">{a.readMinutes} min read</span>
                                    <span className="inline-flex items-center gap-1 text-sm text-brand-navy link-underline">Read <ArrowUpRight size={14} /></span>
                                </div>
                            </Link>
                        ))}
                    </div>
                    {filtered.length === 0 && (
                        <p className="mt-8 text-sm text-brand-ink/55 italic">No articles in this category yet.</p>
                    )}
                </div>
            </section>
        </div>
    );
}
